import { OryOidcIdentityCustomClaimsDecorator } from './oauth2';

/** Registration flow configuration. */
type OryIamConfigRegistration = {
  /** Whether email/phone verification is required after registration. */
  verificationRequired: boolean;
  /** Whether a session is automatically created after successful registration. */
  sessionCreatedOnRegistration: boolean;
}

/** Ory Kratos configuration for identity and authentication flows. */
export type OryIamConfigBase = {
  /** Ory Kratos API endpoint URL. */
  iamApiEndpoint: string;
  /** Axios base options (e.g., withCredentials, headers). */
  baseOptions?: unknown;
  /** Registration flow behavior configuration. */
  registration?: OryIamConfigRegistration
}

/** Ory Hydra configuration for OAuth2/OIDC flows. */
export type OryIamConfigOidc ={
  /** Lambda proxy endpoint URL for OAuth2 operations. */
  oauthApiEndpoint: string;
  /** Authorization endpoint path (default: /oauth/authorize). */
  pathAuthorize?: string;
  /** Consent endpoint path (default: /oauth/consent). */
  pathConsent?: string;
  /** Login endpoint path (default: /oauth/login). */
  pathLogin?: string;
  /** Logout endpoint path (default: /oauth/logout). */
  pathLogout?: string;
  /** Custom OIDC claims decorator for application-specific claims. */
  customIdentityClaimsDecorator?: new () => OryOidcIdentityCustomClaimsDecorator<unknown, unknown>;
  
  /**
   * OAuth2 login session remember duration (in seconds).
   * 
   * Controls how long Hydra remembers a user's authentication for prompt=none support.
   * Only applied when user chooses to remember login (remember=true).
   * 
   * Security considerations:
   * - Shorter durations: Better security (more frequent verification)
   * - Longer durations: Better UX (fewer re-authentications)
   * 
   * Recommended values:
   * - Development: 30 days (2592000 seconds)
   * - Production: 7-14 days (604800-1209600 seconds)
   * - High security: 1 day (86400 seconds)
   * 
   * @example
   * loginRememberSeconds: 7 * 24 * 60 * 60  // 7 days
   */
  loginRememberSeconds?: number;
  
  /**
   * OAuth2 consent session remember duration (in seconds).
   * 
   * Controls how long Hydra remembers a user's consent decision.
   * Only applied when user chooses to remember consent (remember=true).
   * 
   * - undefined: Hydra uses its default behavior (often indefinite for first-party apps)
   * - 0: Remember for browser session only
   * - number: Explicit duration in seconds
   * 
   * Privacy considerations:
   * - Shorter durations: More frequent consent review (better privacy)
   * - Longer durations: Fewer consent prompts (better UX)
   * - Omit (undefined): Indefinite (recommended for first-party apps)
   * 
   * Recommended values:
   * - First-party apps: undefined (indefinite) or 90+ days
   * - Third-party apps: 30 days (2592000 seconds)
   * - Sensitive scopes: 7 days (604800 seconds)
   * 
   * @example
   * consentRememberSeconds: undefined           // indefinite (first-party)
   * consentRememberSeconds: 90 * 24 * 60 * 60  // 90 days
   * 
   * @default undefined
   */
  consentRememberSeconds?: number;
}

/** Configuration for Ory Kratos and Hydra endpoints and behavior. */
export abstract class OryIamConfig {
  abstract base: OryIamConfigBase;
  abstract oidc: OryIamConfigOidc;
}


